import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { LINE_ITEMS_CONTAINER_SX } from '../utils/lineItemCounter';
import { colors, formatCurrency } from '../constants';

/**
 * Column order shared by every line row rendered inside this table. A row must
 * render exactly one cell per column, in this order.
 */
// The table uses a fixed layout, so these widths alone decide the columns:
// opening a row's collapsible section must not resize every other row. The
// Item column takes whatever width is left.
export const LINE_ITEM_COLUMNS = [
  { key: 'toggle', label: '', width: 72 },
  { key: 'item', label: 'Item', align: 'left' },
  { key: 'quantity', label: 'Quantity', align: 'right', width: 120 },
  { key: 'currentQuantity', label: 'Current Quantity', align: 'right', width: 120 },
  { key: 'totalQuantity', label: 'Total Quantity', align: 'right', width: 110 },
  { key: 'unitCost', label: 'Unit Cost', align: 'right', width: 120 },
  { key: 'lineTotal', label: 'Line total', align: 'right', width: 110 },
  { key: 'currentCost', label: 'Current Cost', align: 'right', width: 110 },
  { key: 'newCost', label: 'New Cost', align: 'right', width: 120 },
  { key: 'price', label: 'Price', align: 'right', width: 120 },
  { key: 'remove', label: '', width: 56 },
];

export const LINE_ITEM_COLUMN_COUNT = LINE_ITEM_COLUMNS.length;

const columnWidth = (key) => LINE_ITEM_COLUMNS.find((c) => c.key === key).width;

// The details row spans every column; its padding skips the toggle and remove
// columns so its first field lines up with the Item cell's input and its last
// field ends where the Price input ends.
export const LINE_ITEM_DETAILS_BODY_SX = {
  py: 2,
  pl: `${columnWidth('toggle')}px`,
  pr: `${columnWidth('remove')}px`,
};
export const LINE_ITEM_DETAILS_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
  gap: 1.5,
};

// Narrowest the Item column may get before the table scrolls sideways instead,
// so its search box and scan button stay usable on small screens.
const ITEM_COLUMN_MIN_WIDTH = 260;
const TABLE_MIN_WIDTH =
  ITEM_COLUMN_MIN_WIDTH +
  LINE_ITEM_COLUMNS.reduce((sum, column) => sum + (column.width ?? 0), 0);

const WRAPPER_SX = {
  overflowX: 'auto',
  border: `1px solid ${colors.border}`,
  borderRadius: 1,
};
const TABLE_SX = { tableLayout: 'fixed', width: '100%', minWidth: TABLE_MIN_WIDTH };
const HEAD_CELL_SX = {
  backgroundColor: colors.gray100,
  color: colors.gray600,
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const TOTAL_ROW_SX = {
  '& td': {
    backgroundColor: colors.gray100,
    fontWeight: 700,
    borderBottom: 0,
  },
};
const totalCellIndex = (key) => LINE_ITEM_COLUMNS.findIndex((c) => c.key === key);

/**
 * Shopify-style receive table: an "Add item" action above it, one row per
 * line, and a Total row. Rows are passed as children and render their own
 * main and collapsible advanced rows.
 */
export default function LineItemsTable({
  children,
  itemCount,
  totalQuantity,
  totalCost,
  onAddItem,
  emptyMessage,
}) {
  const quantityIndex = totalCellIndex('quantity');
  const lineTotalIndex = totalCellIndex('lineTotal');

  return (
    <>
      <Button
        size="small"
        startIcon={<AddCircleOutlineIcon />}
        onClick={onAddItem}
        sx={{
          display: 'flex',
          ml: 'auto',
          mb: 1,
          textTransform: 'none',
          fontWeight: 600,
          color: colors.primary,
        }}
      >
        Add item
      </Button>
      <Box sx={WRAPPER_SX}>
        <Table size="small" sx={TABLE_SX}>
          <TableHead>
            <TableRow>
              {LINE_ITEM_COLUMNS.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{ ...HEAD_CELL_SX, width: column.width }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={LINE_ITEMS_CONTAINER_SX}>
            {itemCount === 0 ? (
              <TableRow>
                <TableCell colSpan={LINE_ITEM_COLUMN_COUNT}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.gray400, py: 3, textAlign: 'center' }}
                  >
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              children
            )}
            {itemCount > 0 && (
              <TableRow sx={TOTAL_ROW_SX}>
                {LINE_ITEM_COLUMNS.map((column, index) => (
                  <TableCell key={column.key} align={column.align}>
                    {column.key === 'item' && 'Total'}
                    {index === quantityIndex && totalQuantity}
                    {index === lineTotalIndex && formatCurrency(totalCost)}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </>
  );
}
